import { useCallback, useEffect, useRef, useState } from 'react';
import { thisScope, thisScopeCapitalised, thisScopePossessive } from '@/pages/scopeNoun';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Check } from 'lucide-react';
import { backend } from '@/api/backend';
import { explainLine } from '@/api/explain';
import { ApiError, newIdempotencyKey } from '@/api/http';
import { useBucketToolDetections, useCapabilities } from '@/api/hooks';
import { Button, EmptyState, Panel, PanelHeader, Stamp, Loading } from '@/components/ui';
import { WalkProgress, type Frame, type WalkState } from '@/components/WalkProgress';
import { BucketCharts } from '@/components/BucketCharts';
import { SchemaClusters } from '@/components/SchemaClusters';
import { Archives } from '@/components/Archives';
import { SourceCollections } from '@/components/SourceCollections';
import { DomainGraph } from '@/components/DomainGraph';
import { ContentReaderRole } from '@/components/ContentReaderRole';
import { Details } from '@/components/StepCard';
import { SYNC_EVENTS, syncStreamUrl, useLiveStream } from '@/api/stream';
import { isUnwalked } from './walkVerdict';
import { noReadGrant as grantRefused } from './readGrant';
import { walkDispatchAnswer } from './walkDispatch';
import { rewalkSentence, rewalkVerdict } from './rulesSince';
import { TITLES, currentStage, nextAction, stagesFor, type StageId, type WorkflowFacts } from './workflow';
import { Working } from '@/components/Working';
import { WorkflowRail } from '@/components/WorkflowRail';
import { ToolIcon } from '@/components/ToolIcon';
import { cn } from '@/lib/cn';
import { toolLabel } from '@/lib/toolLabel';
import { AttributionLedger } from '@/components/AttributionLedger';
import { ReconstructionRequests } from '@/components/ReconstructionRequests';
import { PromoteDetection, FAMILIES_REQUIRED, SIGNALS_REQUIRED } from '@/components/PromoteDetection';
import { say } from '@/lib/vocabulary';
import { formatBytes } from '@/lib/format';

/**
 * The guided flow. One bucket = one pipeline, drawn as the six stages the
 * whole product shares:
 *
 *   Choose the data -> List what is there -> Trace it to software ->
 *   Check what was found -> Build the package -> Hand it over
 *
 * The rail, the banner and the ladder are three views of one position, in one
 * set of words. They named the stages differently until now, so a page that
 * said "Build the package" at the top listed "Package and deliver" at the
 * bottom, and an operator had to work out that these were the same thing.
 *
 * Every step's state is computed from live backend reads, never assumed.
 * The current step carries the one primary action; finished pipelines hand
 * off to the next bucket so the user is always told where to go, not left
 * to discover it.
 */

const retry = (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2);

const fmt = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

/**
 * A step's state, in the workflow spine's own vocabulary.
 *
 * This page draws the six-stage rail and then a detailed step list beneath
 * it, and the list had invented a fourth word of its own because the spine
 * had no way to say a stage had failed. Two progress vocabularies on one
 * page leaves a reader deciding which is authoritative. The spine has a
 * failure state now, so this uses its words: the rail is the journey, the
 * list is what each stage needs, and both say the same thing when they agree.
 */
type StepState = Extract<import('./workflow').StageStatus, 'done' | 'current' | 'blocked' | 'failed' | 'ahead'>;

function Marker({ state, last }: { state: StepState; last: boolean }) {
  return (
    <span className="flex w-6 flex-none flex-col items-center self-stretch">
      <span
        className={cn(
          'mt-4 grid h-5 w-5 flex-none place-items-center rounded-full border',
          state === 'done' && 'border-ok bg-ok/15 text-ok',
          state === 'current' && 'border-acc bg-acc-soft text-acc-2',
          state === 'blocked' && 'border-warn bg-warn-soft text-warn',
          state === 'ahead' && 'border-line-2 bg-panel-2 text-ink-4',
        )}
      >
        {state === 'done' ? (
          <Check className="h-3 w-3" aria-hidden="true" />
        ) : state === 'current' ? (
          <span className="h-2 w-2 animate-pulse rounded-full bg-acc" />
        ) : (
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
        )}
      </span>
      {!last && <span className="w-px flex-1 bg-line" />}
    </span>
  );
}

/**
 * One stage of the pipeline, showing its controls only when it is the stage
 * to act on.
 *
 * Every step rendered every control it had, all at once, so a bucket page
 * presented eight stages of decisions simultaneously and an operator had to
 * work out which of them was theirs. The page called itself guided and its
 * own docstring said the current step carries the one primary action; it
 * showed all of them.
 *
 * A finished stage collapses to what it achieved, and opens again on a press,
 * because "walk it again" is a real thing to want and hiding it would trade
 * one failure for another. A stage not yet reached says what unlocks it and
 * offers nothing, since a control that cannot work is worse than no control.
 */
function Step({
  state,
  title,
  sub,
  last = false,
  anchor,
  children,
}: {
  state: StepState;
  title: string;
  sub: string;
  last?: boolean;
  /**
   * The name the workflow's next action uses to point here.
   *
   * `nextAction` has always returned `/buckets/{id}#walk` and
   * `#confirm-tools`, and no element in the app carried either id. Three
   * surfaces offered those buttons - the bucket rail, the pipeline list and
   * the home banner - and every one of them was a link to an anchor that did
   * not exist, so pressing the single most prominent control in the product
   * navigated to the page already loaded and did nothing at all.
   *
   * Landing here is not enough either: a finished stage collapses, so the
   * anchor would have arrived at a row with its control hidden. A targeted
   * step opens itself and scrolls into view.
   */
  anchor?: string;
  children?: React.ReactNode;
}) {
  const acts = state === 'current' || state === 'blocked' || state === 'failed';
  const [open, setOpen] = useState(false);
  const { hash } = useLocation();
  const targeted = Boolean(anchor) && hash === `#${anchor}`;
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (targeted) box.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [targeted]);
  const shown = acts || open || targeted;
  const canOpen = Boolean(children) && state === 'done';
  return (
    <div id={anchor} ref={box} className="flex gap-3 px-4">
      <Marker state={state} last={last} />
      <div className={cn('min-w-0 flex-1 py-3', !last && 'border-b border-line')}>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <span className={cn('text-row font-medium', state === 'ahead' ? 'text-ink-3' : 'text-ink')}>{title}</span>
          <span className={cn('text-small', state === 'blocked' ? 'text-warn' : 'text-ink-3')}>{sub}</span>
          {canOpen && (
            <button
              type="button"
              onClick={() => setOpen(!open)}
              aria-expanded={open}
              className="ml-auto flex-none text-caption text-acc-2 underline-offset-2 hover:underline"
            >
              {open ? 'hide' : 'change this'}
            </button>
          )}
        </div>
        {children && shown && <div className="mt-2">{children}</div>}
      </div>
    </div>
  );
}

/**
 * Work that is available from this page but is not a stage of this bucket.
 *
 * Reconstruction and whole-company analysis were drawn as steps in the spine,
 * with markers, and each decided on its own whether it was where the operator
 * stood. So a page whose banner named one action showed a six-stage rail, an
 * eight-row ladder in different words, and two rows wearing the current
 * marker at once. Neither of these is a stage of this bucket - they run for
 * the company - so they carry no position and cannot claim one.
 */
function Aside({ title, sub, children }: { title: string; sub: string; children?: React.ReactNode }) {
  return (
    <div className="border-b border-line px-4 py-3 last:border-0">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <span className="text-row font-medium text-ink">{title}</span>
        <span className="text-small text-ink-3">{sub}</span>
      </div>
      {children && <div className="mt-2">{children}</div>}
    </div>
  );
}

/**
 * The tools a walk found, named.
 *
 * A sealed walk reported "4 tools", which is a count of something the operator
 * cannot see and cannot act on. The labels come back in the same payload.
 * Bounded, because a bucket that seals sixteen tools would otherwise push the
 * counts off the line.
 */
function namedTools(entries: { display?: { label?: string }; toolId: string; objects: number }[]): string {
  if (entries.length === 0) return 'no tool identified';
  const bySize = [...entries].sort((a, b) => b.objects - a.objects);
  const names = bySize.map((e) => e.display?.label ?? toolLabel(e.toolId));
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;
}

/** Latest-first sync history read; the newest run is the scope's truth. */
function useSyncRuns(bucketId: string | undefined) {
  return useQuery({
    queryKey: ['sync-runs', bucketId],
    queryFn: ({ signal }) => backend.listSyncRuns(bucketId!, signal),
    enabled: Boolean(bucketId),
    retry,
    refetchInterval: (q) => {
      const s = q.state.data?.items[0]?.state;
      return !s || (s !== 'COMPLETE' && s !== 'FAILED' && s !== 'ACCESS_BLOCKED') ? 10_000 : 60_000;
    },
  });
}

function useToolset(bucketId: string | undefined) {
  return useQuery({
    queryKey: ['toolset', bucketId],
    queryFn: ({ signal }) => backend.getBucketToolset(bucketId!, signal),
    enabled: Boolean(bucketId),
    retry,
    // Until sealed, keep watching: the walk dispatches unattended. A retryable
    // failure keeps polling too, because the API is briefly unavailable during
    // a backend roll and the breakdown must return on its own. A terminal
    // refusal does not: a 403 or a 501 will answer the same way forever, and
    // re-asking every fifteen seconds is load with no chance of an answer.
    refetchInterval: (q) => {
      if (q.state.data?.sealedAt) return false;
      const e = q.state.error;
      if (e instanceof ApiError && !e.retryable) return false;
      return 15_000;
    },
    refetchOnWindowFocus: true,
  });
}

export function BucketFlowPage() {
  const { bucketId } = useParams();
  const qc = useQueryClient();
  const goto = useNavigate();

  const bucket = useQuery({
    queryKey: ['flow-bucket', bucketId],
    queryFn: ({ signal }) => backend.getBucket(bucketId!, signal),
    enabled: Boolean(bucketId),
    retry,
  });
  const syncs = useSyncRuns(bucketId);
  const toolset = useToolset(bucketId);
  const companyId = bucket.data?.bucket.companyId;
  const tools = useBucketToolDetections(bucketId);
  /**
   * The walker rule registry, which decides whether walking again can help.
   *
   * A client method for this has existed since the contract was pinned and
   * nothing has ever called it. It is company-independent and changes rarely,
   * so it is cached for an hour and shared by every bucket page.
   */
  /**
   * Whether this scope can actually be read.
   *
   * Until this route existed, a walk that produced nothing looked exactly like
   * a walk about to start, and the truest sentence the product could manage was
   * that nothing had reported for two minutes - with a guess attached about the
   * likely cause. The guess was usually right and it was still a guess. This is
   * the fact, so the page can stop hedging.
   */
  const grants = useQuery({
    queryKey: ['content-reader-grants', companyId],
    queryFn: ({ signal }) => backend.listContentReaderGrants(companyId!, signal),
    enabled: Boolean(companyId),
    retry,
    staleTime: 60_000,
  });
  const grant = (grants.data?.items ?? []).find((g) => g.bucketId === bucketId) ?? null;
  /**
   * True only on a definite no; the rule lives in `readGrant.ts` so the page
   * and its tests read the same one.
   */
  /**
   * Folder or bucket. Half the scopes this product holds are folders, and the
   * page called every one of them "this bucket" while the rail above it
   * correctly read `bucket/folder`.
   */
  const prefix = bucket.data ? ((bucket.data as { prefix?: string }).prefix ?? '') : '';
  const noReadGrant = grantRefused(
    grants.isSuccess ? 'success' : grants.isError ? 'error' : 'pending',
    grants.data?.items ?? [],
    bucketId,
  );

  const rules = useQuery({
    queryKey: ['attribution-rules'],
    queryFn: ({ signal }) => backend.listAttributionRules(signal),
    retry,
    staleTime: 3_600_000,
  });

  /**
   * The outcome of the last thing the operator pressed.
   *
   * It was one grey sentence in the ladder, in the same ink as the state text
   * around it, so "The API did not respond" read as a caption. A failed
   * dispatch and a successful one looked identical, and both looked like
   * description. The tone is carried with it now, and it is shown next to the
   * control that caused it.
   */
  const [note, setNote] = useState<string | null>(null);
  const [noteFailed, setNoteFailed] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  // A listing publishes its own progress. This page polled every ten seconds,
  // so a listing that completed in under that still showed as running.
  const activeSync = syncs.data?.items.find(
    (r) => r.state !== 'COMPLETE' && r.state !== 'FAILED' && r.state !== 'ACCESS_BLOCKED',
  );
  useLiveStream(activeSync ? syncStreamUrl(activeSync.syncRunId) : null, SYNC_EVENTS, () => {
    void qc.invalidateQueries({ queryKey: ['sync-runs', bucketId] });
  });

  const syncNow = async () => {
    setBusy('sync');
    setNote(null);
    setNoteFailed(false);
    try {
      const r = await backend.startSyncRun(bucketId!, newIdempotencyKey());
      setNote(
        r.disposition === 'ACTIVE_RUN_RETURNED'
          ? 'A listing was already running, so this joined it rather than starting a second one.'
          : 'Listing started. It reports here as it runs.',
      );
      void qc.invalidateQueries({ queryKey: ['sync-runs', bucketId] });
    } catch (e) {
      setNoteFailed(true);
      setNote(explainLine(e, 'list this bucket'));
    } finally {
      setBusy(null);
    }
  };

  // The live stream seals before the sealed-toolset projection is readable.
  // Holding the terminal frame lets the pipeline advance on the walk's own
  // authority instead of showing "unlocks at seal" under a SEALED strip.
  /**
   * A walk was dispatched and has not shown itself yet.
   *
   * The dispatch answers in a moment; the walk takes longer to appear on the
   * stream. In that gap `busy` was already false and the stream was not yet
   * live, so on a sealed bucket the strip's own guard unmounted it - and the
   * message said "it appears in the strip below" while there was no strip
   * below. This holds the strip on screen across the gap.
   */
  const [dispatched, setDispatched] = useState(false);
  const [liveSeal, setLiveSeal] = useState<Frame | null>(null);
  const [streamState, setStreamState] = useState<WalkState>('connecting');
  const onSealed = useCallback(
    (frame: Frame) => {
      setLiveSeal(frame);
      setDispatched(false);
      void qc.invalidateQueries({ queryKey: ['toolset', bucketId] });
      void qc.invalidateQueries({ queryKey: ['company-tools'] });
    },
    [qc, bucketId],
  );

  const walkNow = async () => {
    setBusy('walk');
    setNote(null);
    try {
      const answer = walkDispatchAnswer(await backend.dispatchWalk(bucketId!, newIdempotencyKey()));
      setNoteFailed(!answer.started);
      setDispatched(answer.started);
      setNote(answer.detail);
      // A finished stage collapses, so on a bucket being walked AGAIN the
      // strip the sentence above promises was inside a closed row. Naming the
      // step opens it and scrolls it into view.
      goto(`#walk`, { replace: true });
      void qc.invalidateQueries({ queryKey: ['toolset', bucketId] });
    } catch (e) {
      setNoteFailed(true);
      setNote(explainLine(e, 'do that'));
    } finally {
      setBusy(null);
    }
  };

  const [sweep, setSweep] = useState<boolean | null>(null);
  const toggleSweep = async (next: boolean) => {
    setBusy('sweep');
    try {
      const r = await backend.setSweepSelection(bucketId!, next);
      setSweep(r.sweepSelected);
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : 'The API did not respond.');
    } finally {
      setBusy(null);
    }
  };


  // Every hook must run on every render, so this sits with the others and
  // above the early returns below. Placing it after them shipped a crash: on
  // the first render the page bailed out early and ran fewer hooks, and React
  // tore the tree down the moment the bucket arrived and this one appeared.
  //
  // The backend now lists a bucket's runs, so a part-built package is found by
  // asking rather than by whatever this browser happened to remember. That
  // stopgap is gone: it never helped a colleague or a second machine.
  const packagingRuns = useQuery({
    queryKey: ['packaging-runs', bucketId],
    queryFn: ({ signal }) => backend.listPackagingRuns(bucketId!, signal),
    enabled: Boolean(bucketId),
    retry,
    staleTime: 15_000,
  });
  const latestRun = (
    (packagingRuns.data as { items?: { id: string; state: string; supersededBy?: unknown }[] } | undefined)?.items ?? []
  ).find((r) => !r.supersededBy);
  const packagingState = latestRun?.state ?? null;

  if (bucket.isPending)
    return <Loading what="Reading this bucket" rows={6} />;
  if (bucket.isError || !bucket.data)
    return <EmptyState title="Bucket unavailable" description="This bucket does not exist or the API did not respond." />;

  const b = bucket.data;
  const latestSync = syncs.data?.items[0];
  const sealedAt = toolset.data?.sealedAt ?? null;
  const rewalk = rewalkVerdict(sealedAt, rules.data?.items ?? null);
  const entries = toolset.data?.entries ?? [];
  const stray = toolset.data?.stray;
  const conservation = toolset.data?.conservation;

  /**
   * Detections, and whether we actually know about them.
   *
   * This read answers 500 in production today, and the page rendered that as
   * "no registered detections yet for this scope" - a definite zero, on a walk
   * that had just sealed four tools over 506 objects - and then advised walking
   * again, which cannot help because the walk was never the problem. An unread
   * value must never be drawn as a counted one.
   */
  const detectionsKnown = tools.isSuccess;
  // Already this bucket's: the route returns no other bucket's detections.
  const bucketTools = tools.data?.items ?? [];
  const backed = bucketTools.filter((t) => t.status === 'EVIDENCE_BACKED');
  const candidates = bucketTools.filter((t) => t.status === 'CANDIDATE');

  // ---- step states, each from real reads ----
  const observeState: StepState = !syncs.data
    ? 'ahead'
    : !latestSync
      ? 'current'
      : latestSync.state === 'COMPLETE'
        ? 'done'
        : latestSync.state === 'FAILED' || latestSync.state === 'ACCESS_BLOCKED'
          ? 'blocked'
          : 'current';
  // Sealed on either authority: the projection once readable, the walk's own
  // terminal event until then.
  const sealed = Boolean(sealedAt) || Boolean(liveSeal);
  // "Nothing has walked this scope" is a claim about the backend, so it
  // needs the backend to say it. Two independent authorities must agree: a
  // SUCCESSFUL toolset read that carries no seal, and the walk stream's own
  // no-walk verdict. Inferring it from a read that merely failed or had not
  // answered yet is how a sealed bucket gets reported as stuck - the toolset
  // route times out often enough that this is the common case, not the rare
  // one.
  const observedAt = latestSync?.state === 'COMPLETE' ? Date.parse(latestSync.requestedAt) : null;
  const waitedMinutes = observedAt ? Math.floor((Date.now() - observedAt) / 60_000) : 0;
  const overdue = isUnwalked({
    sealed,
    toolsetRead: toolset.isSuccess ? 'success' : toolset.isError ? 'error' : 'pending',
    stream: streamState,
    observedAt,
  });
  const walkState: StepState = sealed
    ? 'done'
    : // A walk the stream reported as failed was drawn with the pulsing
      // marker that means "running now", so the one state an operator most
      // needs to act on looked exactly like the one that needs nothing.
      streamState === 'failed' || streamState === 'error'
      ? 'blocked'
      : overdue
        ? 'blocked'
        : observeState === 'done'
          ? 'current'
          : 'ahead';
  /**
   * Review used to decide on its own that it was current, which on a scope
   * with no registered detections at all was always. The rail computes one
   * position for the whole page; this reads it rather than voting against it.
   */
  const reviewState = (here: string): StepState => (!sealed ? 'ahead' : here === 'review' ? 'current' : 'done');
  /**
   * The one stage the rail says the operator is at.
   *
   * Explore, analysis and packaging each decided independently that they were
   * current, so three of them expanded their controls at once under a banner
   * naming a single next action. Availability and position are different
   * facts: all three can be available while only one is where you are. The
   * rail already computes the position, so the steps read it rather than each
   * guessing.
   */
  const railFacts: WorkflowFacts = {
    hasContext: true,
    known: toolset.isSuccess || toolset.isError,
    observed: latestSync?.state === 'COMPLETE',
    observing: Boolean(activeSync),
    sealed,
    walking: streamState === 'live',
    toolCount: entries.length || liveSeal?.tools.length || 0,
    // A candidate at or above the bar is one attestation from being placeable;
    // a confirmed one already is. Packaging with none of the second builds an
    // empty package.
    // Null is not zero: with the detections read failed, "nothing is
    // confirmed" is a claim this page cannot make.
    sealedObjects: toolset.data?.conservation?.inventoryObjectCount ?? null,
    listedObjects: latestSync?.state === 'COMPLETE' ? (latestSync.listed ?? null) : null,
    promotable: detectionsKnown
      ? candidates.filter((t) => t.evidence.signals >= SIGNALS_REQUIRED && t.evidence.families >= FAMILIES_REQUIRED).length
      : null,
    supported: detectionsKnown ? backed.length : null,
    packagingState: packagingState ?? null,
    exportsBuilt: false,
    // The bucket page does not read the packaging gate, so it says it does not
    // know rather than guessing at an empty package.
    populationRegistered: null,
    // This page does not read the delivery record.
    deliveryState: null,
  };
  const stages = stagesFor(railFacts);
  const here = currentStage(stages).id;
  /**
   * The one action the banner is already offering.
   *
   * The banner said "Continue the package: choose what ships" and the ladder
   * row sixty pixels below said "Open packaging" - the same route, in
   * different words, presented as two decisions. A stage does not repeat the
   * move the banner is already making; it repeats it only when the banner is
   * pointing somewhere else.
   */
  const primary = nextAction(railFacts, bucketId ?? null);
  /**
   * The banner performs listing and walking itself, because they happen on
   * this page; everything else it links to. Either way the stage below never
   * offers the same move a second time.
   */
  /**
   * Read from what the action POINTS AT, not from where the rail says we are.
   *
   * Keying on the stage missed the case the product spends most of its time
   * in: a sealed bucket whose detections are all short of the evidence bar,
   * where the stage is "Check what was found" and the named action is "Walk
   * this bucket again". That action is a walk, this page runs walks, and
   * because the stage did not match it fell through to a link - to an anchor
   * that did not exist.
   */
  const wants = primary?.to.includes('#') ? primary.to.slice(primary.to.indexOf('#')) : '';
  const act =
    noReadGrant
      ? null
      : /**
         * "Read the detections again" has to actually read them again.
         *
         * When the detections read fails, the workflow's next action is that
         * sentence pointing at `#confirm-tools` - and the banner renders any
         * action it is not given a handler for as a link. So the most
         * prominent control on the page moved the hash, scrolled to the step
         * that reports the same failure, and never re-issued the request. It
         * was a retry with no retry behind it: pressing it looked like
         * nothing happening, because nothing did.
         *
         * This is a refetch rather than a navigation, so it is checked before
         * the two dispatches below - a failed read is the reason the page
         * cannot say what stage it is on, and it outranks them.
         */
        !detectionsKnown && !tools.isPending
        ? {
            // A retry that fails again must say so. Silence after a press is
            // indistinguishable from a control that does nothing, which is
            // what this looked like when it was a link.
            onClick: () => {
              setNote(null);
              setNoteFailed(false);
              void tools.refetch().then((r) => {
                if (r.isError) {
                  setNote(explainLine(r.error, 'read the detections'));
                  setNoteFailed(true);
                }
              });
            },
            busy: tools.isFetching,
          }
        : primary && here === 'observe' && observeState !== 'blocked'
      ? { onClick: () => void syncNow(), busy: busy === 'sync' || Boolean(activeSync) }
      : primary && (wants === '#walk' || (here === 'attribute' && walkState === 'current'))
        ? { onClick: () => void walkNow(), busy: busy === 'walk' || streamState === 'live' }
        : null;
  const outcome = note ? (
    <p
      role={noteFailed ? 'alert' : 'status'}
      className={`mt-2 text-small ${noteFailed ? 'text-warn' : 'text-ink-2'}`}
    >
      {note}
    </p>
  ) : null;
  const working =
    busy === 'sync' || activeSync ? (
      <>
        <Working
          what={busy === 'sync' ? 'Asking the backend to list this bucket' : 'Listing this bucket'}
          usually="a listing usually finishes within about 2 minutes"
          overAt={240}
          since={activeSync ? Date.parse(activeSync.requestedAt) : undefined}
        />
        {/*
          * The count, while it counts.
          *
          * The listing streams its own events and every one of them only
          * invalidated a query - so the run record's climbing numbers were
          * fetched and never drawn, and a listing in progress showed a spinner
          * and an elapsed clock over a bucket whose file count was already
          * known and rising. The walk and the delivery both draw theirs.
          */}
        {activeSync && (
          <p className="mono mt-1 text-caption text-ink-2" aria-live="polite">
            {activeSync.listed.toLocaleString()} objects listed · +{activeSync.new.toLocaleString()} new · ~
            {activeSync.changed.toLocaleString()} changed · {activeSync.unchanged.toLocaleString()} unchanged
          </p>
        )}
      </>
    ) : busy === 'walk' || streamState === 'live' ? (
      <Working
        what={busy === 'walk' ? 'Asking the backend to walk this bucket' : 'Reading every file and naming the software that made it'}
        usually="a walk usually finishes within about 10 minutes"
        overAt={1800}
      />
    ) : null;
  /**
   * Whether the banner is already making this stage's move.
   *
   * Keyed on the rail position alone, this said "no" for the re-walk: the
   * banner was running the walk while the stage read 'review', so the outcome
   * line was printed twice on one screen. The banner's target is what decides.
   */
  const offered = (id: StageId) =>
    (id === 'package' && Boolean(primary?.to.startsWith(`/buckets/${bucketId}/package`))) ||
    (act !== null && id === 'attribute' && wants === '#walk') ||
    (act !== null && id === here);
  const statusOf = (id: StageId): StepState => {
    const st = stages.find((x) => x.id === id)?.status;
    return st === 'running' || st === 'unknown' || st === undefined ? 'ahead' : st;
  };


  return (
    <div className="min-h-0 flex-1 animate-pop space-y-3 overflow-y-auto p-5">
      {/* The page named itself only through a 12px breadcrumb, so the bucket an
          operator is working in was the smallest text on screen. */}
      <h1 className="mono text-h2 text-ink">{b.bucket.name}</h1>
      {/* The same six stages render here and inside packaging, from the same
          facts, so moving between them never changes the metaphor. The steps
          below are not a second journey: they are what the current stage
          needs, in the same words. */}
      <WorkflowRail
        bucketId={b.bucket.id}
        facts={railFacts}
        act={act ? { ...act, note: working ?? outcome } : null}
      />

      {/* A crumb reading "Pipelines >" put a catalogue of every other bucket
          one click from the top of this page, and repeated this bucket's name
          under the heading that already says it. What is left is the one thing
          the heading does not carry: where this bucket stands. The rail names
          the bucket, switches to another, and holds the graph. */}
      <div className="flex flex-wrap items-center gap-2 text-small text-ink-3">
        <Stamp tone={sealed ? 'ok' : 'mut'}>{sealed ? 'SEALED' : 'IN PIPELINE'}</Stamp>
      </div>

      <Panel>
        {/* The rail above already names where this bucket is, and this said
            so a second time sixty pixels below it, in a different set of
            stage names: the rail's "Check what was found" is this list's
            "Evidence grading". Two position indicators in two vocabularies
            leave a reader working out which one to believe. This is the
            detail of the stage the rail names, so it says that. */}
        <PanelHeader title="What each stage needs" sub="the same six stages as the rail above, each reporting its own live backend state" />

        <Step state="done" title={TITLES.context.title} sub={`account ${b.accountId} · ${b.bucket.region} · ${b.prefix || 'whole bucket'}`} />

        <Step
          state={observeState}
          title={TITLES.observe.title}
          sub={
            !latestSync
              ? 'first sync lands within 2 minutes of registration'
              : latestSync.state === 'COMPLETE'
                ? `re-observed ${fmt(latestSync.requestedAt)} · ${latestSync.listed.toLocaleString()} objects listed`
                : latestSync.state === 'FAILED' || latestSync.state === 'ACCESS_BLOCKED'
                  ? `last sync ${latestSync.state.toLowerCase().replace('_', ' ')} · check the scope's read role`
                  : 'listing the scope now'
          }
        >
          {!offered('observe') && (
            <div className="flex items-center gap-3">
              <Button variant="ghost" loading={busy === 'sync'} onClick={() => void syncNow()}>
                {/* "again" on a bucket nothing has ever listed tells an
                    operator they have already done something they have not. */}
                {busy === 'sync' ? 'Requesting…' : latestSync ? 'List this bucket again' : 'List this bucket'}
              </Button>
            </div>
          )}
          {!offered('observe') && outcome}
        </Step>

        <Step
          state={walkState}
          anchor="walk"
          title={TITLES.attribute.title}
          sub={
            sealedAt
              ? // "4 tools" is a count of a thing an operator cannot see. The
                // names are in the same answer and are what the question
                // "what did it find" is actually asking.
                `sealed ${fmt(sealedAt)} · ${namedTools(entries)} · ${entries
                  .reduce((n, e) => n + e.objects, 0)
                  .toLocaleString()} of ${((stray?.objects ?? 0) + entries.reduce((n, e) => n + e.objects, 0)).toLocaleString()} files traced`
              : liveSeal
                ? `sealed just now · ${liveSeal.tools.length} tools · ${liveSeal.toolDispositions.toLocaleString()} tool objects · ${liveSeal.strayDispositions.toLocaleString()} stray`
                : overdue
                  ? `observed ${waitedMinutes} minutes ago and still not walked`
                  : walkState === 'current'
                    ? 'dispatches automatically within 10 minutes; watch it below'
                    : 'starts after the first complete sync'
          }
        >
          {/* Stated, not guessed. The grant route answers this directly now,
              so a scope that cannot be read says which of the two it is. */}
          {noReadGrant && (
            <p role="status" className="mt-1 rounded-md border border-warn bg-warn-soft px-3 py-2 text-small text-ink-2">
              This scope has no live content-read grant yet, so a walk over it cannot open a single file. Listing and
              reading are separate grants: the {latestSync?.listed.toLocaleString() ?? ''} files above were listed on the
              listing grant alone.{' '}
              {grant?.revokedAt
                ? `A grant existed and was revoked ${fmt(grant.revokedAt)}. It is minted again automatically, and the sweep that does it runs every ten minutes.`
                : 'One is minted for this scope automatically, by a sweep that runs every ten minutes, so there is nothing to do here but wait for it.'}{' '}
              The walk starts on its own once the grant is live.
            </p>
          )}
          {overdue && !noReadGrant && (
            <p className="mt-1 rounded-md border border-warn bg-warn-soft px-3 py-2 text-small text-ink-2">
              No walk has been recorded for this bucket, and the automatic run happens every ten minutes.{' '}
              {latestSync?.listed.toLocaleString() ?? 'The'} files were listed successfully, so the scope is registered and
              visible.{' '}
              {grants.isSuccess && grant?.granted
                ? `Its read grant is live${grant.grantedAt ? ` since ${fmt(grant.grantedAt)}` : ''}, so the grant is not what is holding this up.`
                : 'The read grant that lets attribution open file contents could not be read, so what is holding this up is not established here.'}
            </p>
          )}
          {/* The strip belongs on screen while a walk can be watched. On a
              bucket whose listing has not finished there is no walk to watch,
              and it sat there reading CONNECTING under a step that had not
              started: motion implying work, above a button offering to start
              the work. */}
          {(observeState === 'done' || busy === 'walk' || dispatched || streamState === 'live') &&
            (!sealedAt || busy === 'walk' || dispatched || streamState === 'live') && (
              <div className="overflow-hidden rounded-md border border-line">
                <WalkProgress bucketId={bucketId!} onSealed={onSealed} onState={setStreamState} />
              </div>
            )}
          <div className="mt-1 flex flex-wrap items-center gap-3">
            {/* The banner at the top of this page says nothing can be traced
                before its files have been seen once, and this button ignored
                it: on a bucket with no completed listing it was live, and
                pressing it dispatches a walk with no manifest to walk. The
                dependency the page already states is the dependency the
                control now honours.

                When the banner is carrying this move, it is not offered twice. */}
            {/* A disabled button is indistinguishable from a broken one: it
                looks pressable, absorbs the press, and says nothing. The
                listing is the only thing that can block a walk from here, so
                where the button cannot work there is a sentence instead of a
                control. */}
            {!offered('attribute') &&
              (streamState === 'live' ? (
                // The rail says "running - nothing is needed from you" and this
                // offered to start one anyway, on the same screen.
                <span className="rounded border border-line bg-panel-2 px-2.5 py-1 text-small text-ink-2">
                  A walk is running now; the strip above is following it
                </span>
              ) : noReadGrant ? (
                // Dispatching here is accepted and then reads nothing. The
                // control that changes the outcome is the grant, below.
                <span className="rounded border border-warn bg-warn-soft px-2.5 py-1 text-small text-ink-2">
                  Waiting for this scope&rsquo;s read grant, which is minted automatically
                </span>
              ) : observeState === 'done' ? (
                <Button variant="ghost" loading={busy === 'walk'} onClick={() => void walkNow()}>
                  {busy === 'walk' ? 'Dispatching…' : sealed ? 'Walk again' : 'Walk now'}
                </Button>
              ) : (
                <span className="rounded border border-line bg-panel-2 px-2.5 py-1 text-small text-warn">
                  Cannot walk yet: this bucket has no completed listing
                </span>
              ))}
            <span className="text-caption text-ink-3">
              {/* "instead of waiting for the automatic run" was the only thing
                  on the page naming the other path, and it named it in
                  passing. Both paths are now stated where each control is. */}
              {noReadGrant
                ? 'Registering the read access is a one-time step per scope, and the walk works from then on.'
                : streamState === 'live'
                  ? 'It seals on its own. Starting another would not make this one finish sooner.'
                  : observeState !== 'done'
                    ? 'A walk reads the files the listing found, so it waits for the listing above to finish.'
                    : sealed
                      ? 'Starts immediately and seals a fresh run. The strip above follows it live.'
                      : 'Starts immediately, without waiting for the unattended run.'}
            </span>
          </div>
          {busy === 'walk' && !offered('attribute') && (
            <Working
              what="Asking the backend to start a walk"
              usually="dispatch usually answers within a few seconds; the walk itself then reports above"
              overAt={30}
            />
          )}
          {/* This row has a button and had nowhere to report what pressing it
              did. `note` was rendered in the listing row above, which collapses
              once a listing has completed, so a walk that was refused - the
              common answer on a scope with no content-read grant - said
              nothing at all. Pressing it looked like pressing a dead button. */}
          {!offered('attribute') && outcome}
          {/* Whether repeating this job could change its own outcome. A walk
              reads the same bytes with the rules the collector carries, so an
              unchanged rule set produces the dispositions it produced last
              time. The registry has always said when each rule arrived and
              nothing read it, so this advice was given unconditionally. */}
          {sealed && (
            <p
              className={cn(
                'mt-2 text-caption',
                rewalk.kind === 'SAME_RULES' ? 'text-warn' : rewalk.kind === 'WORTH_IT' ? 'text-ink-2' : 'text-ink-4',
              )}
            >
              {rewalkSentence(rewalk)}
            </p>
          )}
          {/* A walk reads file contents, which needs a role in the account that
              owns the bucket. Registering one used to be SQL only, so a bucket
              without it failed to walk with nothing on screen explaining why.
              Only offered while the bucket has not sealed: on one that just
              walked, "cannot be walked" contradicts the SEALED strip directly
              above it, which is worse than not offering it at all. */}
          {/* Framed as a symptom to debug, this reads as troubleshooting for
              an operator whose walk has already failed. Reading file contents
              needs this role, so on a bucket that has never sealed it is the
              next step rather than a remedy, and it says so. On one that has
              sealed the walk plainly has the access it needs, and the offer
              stays folded away. */}
          {/* Only once walking is the thing to do. Before the listing
              finishes, "Walk not starting?" is troubleshooting for a walk
              nobody could have started. */}
          {!sealed && observeState === 'done' && (
          <Details
            /* Closed even when the grant is missing. It used to spring open on
               exactly the state that no longer needs a person: the provisioner
               mints the role within ten minutes, so opening a form and calling
               it "what unblocks the walk" sends someone to do the one thing the
               product now does for them. It stays available for the case the
               sweep cannot serve - a bucket in an account it holds no power in
               - and closed otherwise. */
            startOpen={false}
            title={
              noReadGrant
                ? 'Read access is being minted for this scope · open only if you are supplying a role yourself'
                : `${thisScopeCapitalised(prefix)}\u2019s read access, if you ever need to supply a role yourself`
            }
          >
            <ContentReaderRole
              bucketId={bucketId!}
              onRegistered={() => void qc.invalidateQueries({ queryKey: ['toolset', bucketId] })}
            />
          </Details>
          )}
          {/* The unattended setting matters once walking is the thing to do.
              Before the listing finishes it is a second decision stacked on a
              step nobody has reached. */}
          {observeState === 'done' && (() => {
            // This bucket's own record, from the read of this bucket. It used
            // to be found by scanning the account-wide listing for its own id
            // - the whole catalogue fetched to answer one question about one
            // row of it, when getBucket returns that exact row.
            const self = b;
            // Read as the typed field it now is. This was an `as unknown as`
            // cast because RegisteredBucket did not declare sweepSelected,
            // while production had served it on every bucket since the sweep
            // shipped. The cast worked, which is the dangerous part: a client
            // reading a field its own contract forbids is one serialiser
            // change away from silently reporting every bucket as off.
            const on = sweep ?? self?.sweepSelected ?? false;
            return (
              /* Two ways a bucket gets walked, and the page showed one of them
                 as a button and the other as a chip that read like a label.
                 An operator could not tell that the chip was a control, that
                 it governed something different from the button beside it, or
                 that turning it off leaves the walk button working. Both are
                 named, both say what they do, and the difference between them
                 is stated once rather than left to be inferred. */
              <div className="mt-2 rounded border border-line bg-panel-2 px-3 py-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <span className="text-caption font-semibold uppercase tracking-wider text-ink-3">
                    Unattended walks
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={on}
                    aria-label={`Nightly walk for this bucket`}
                    onClick={() => void toggleSweep(!on)}
                    disabled={busy === 'sweep'}
                    className={cn(
                      'rounded border px-2 py-0.5 text-caption transition-colors duration-fast disabled:opacity-50',
                      on ? 'border-acc bg-acc-soft text-acc-2' : 'border-line-2 bg-panel text-ink-2 hover:bg-hov',
                    )}
                  >
                    {busy === 'sweep' ? 'saving…' : on ? 'on · turn off' : 'off · turn on'}
                  </button>
                  <span className="min-w-0 flex-1 text-caption text-ink-3">
                    {on
                      ? 'The nightly run re-walks this bucket on its own, about every 18 hours.'
                      : 'The nightly run skips this bucket. It is walked only when someone presses Walk.'}
                  </span>
                </div>
                <p className="mt-1.5 text-caption text-ink-4">
                  This setting governs the unattended run only. Walk above starts one immediately either way, and takes
                  effect whether this is on or off.
                </p>
              </div>
            );
          })()}
          {sealed && entries.length === 0 && !toolset.isError && (
            <div className="mt-1 flex items-center gap-3 rounded-md border border-line px-3 py-2 text-small text-ink-3">
              <span className="h-2 w-2 animate-pulse rounded-full bg-acc" />
              Reading the per-tool file counts and sizes… this read is slow on the API right now and can take up to half a
              minute.
            </div>
          )}
          {toolset.isError && (
            <div className="mt-1 flex flex-wrap items-center gap-3 rounded-md border border-warn bg-warn-soft px-3 py-2 text-small text-ink-2">
              <span className="min-w-0 flex-1">
                The sealed breakdown could not be read
                {''}. The walk's own counts above still
                stand; the per-tool file counts and sizes come from this read.
              </span>
              <Button variant="ghost" onClick={() => void toolset.refetch()}>
                Retry
              </Button>
            </div>
          )}
          {entries.length > 0 && (
            <div className="mt-1 overflow-hidden rounded-md border border-line">
              {entries.map((e) => (
                <Link
                  key={e.toolId}
                  to={`/buckets/${bucketId}/graph`}
                  className="flex items-center gap-3 border-b border-line px-3 py-1.5 text-small transition-colors duration-fast last:border-0 hover:bg-hov"
                >
                  <ToolIcon tool={e.toolId} display={e.display} size={14} />
                  <span className="min-w-0 flex-1 truncate text-ink">{e.display.label}</span>
                  <span className="mono w-24 flex-none text-right text-caption text-ink-2">
                    {e.objects.toLocaleString()} files
                  </span>
                  <span className="mono w-20 flex-none text-right text-caption text-ink-3">
                    {formatBytes(e.bytes)}
                  </span>
                </Link>
              ))}
              {stray && (
                <div className="flex items-center gap-3 border-t border-line bg-panel-2 px-3 py-1.5 text-small">
                  <span className="grid h-3.5 w-3.5 flex-none place-items-center rounded-sm border border-line-2 text-caption text-ink-4">
                    ?
                  </span>
                  <span className="min-w-0 flex-1 truncate text-ink-2">
                    Unattributed
                    <span className="ml-2 text-caption text-ink-4">
                      {stray.reasons
                        .map((r) => `${say(r.reasonKind)} ${r.objects.toLocaleString()}`)
                        .join(' · ')}
                    </span>
                  </span>
                  <span className="mono w-24 flex-none text-right text-caption text-ink-2">
                    {stray.objects.toLocaleString()} files
                  </span>
                  <span className="mono w-20 flex-none text-right text-caption text-ink-3">
                    {formatBytes(stray.bytes)}
                  </span>
                </div>
              )}
              {conservation && (
                <div className="flex items-center gap-3 border-t border-line px-3 py-1.5">
                  <span className="min-w-0 flex-1 text-caption text-ink-3">
                    {conservation.toolObjectCount.toLocaleString()} attributed +{' '}
                    {conservation.strayObjectCount.toLocaleString()} unattributed ={' '}
                    {conservation.inventoryObjectCount.toLocaleString()} files listed
                    {conservation.equationValid === true
                      ? ' · every file accounted for'
                      : conservation.equationValid === false
                        ? ' · the counts do not add up'
                        : ' · completeness not reported'}
                  </span>
                  <span className="mono w-20 flex-none text-right text-caption text-ink-3">
                    {formatBytes(entries.reduce((n, e) => n + e.bytes, 0) + (stray?.bytes ?? 0))}
                  </span>
                </div>
              )}
              {/* The conservation equation above is the total. This is the
                  ledger behind it: the workflow contract asks for every object
                  filterable by the one answer it got, and until now an
                  operator could read that 41,451 files were untraced and could
                  not look at one of them. */}
              {sealed && (
                <Details title="Every file and the one answer it got">
                  <AttributionLedger bucketId={b.bucket.id} />
                </Details>
              )}
            </div>
          )}
        </Step>

        {/* Grading and browsing were two rows, each claiming a position, for
            one stage the rail calls "Check what was found". Reading counts and
            reading records are the same act: confirming what the walk found
            before it is packaged. */}
        <Step
          state={reviewState(here)}
          anchor="confirm-tools"
          title={TITLES.review.title}
          sub={
            !sealed
              ? 'unlocks at seal; each detection then accumulates graded signals'
              : !detectionsKnown
                ? 'the detections for this scope could not be read'
                : bucketTools.length === 0
                  ? 'no registered detections yet for this scope'
                  : `${backed.length} evidence backed · ${candidates.length} candidates accumulating signals`
          }
        >
          {/* A candidate that clears the bar is one attestation away from
              being placeable, and until the promotion route existed there was
              no way to make it from the product. The bar reads two kinds of
              check, not five: it was recalibrated, and a candidate cannot be
              told it is short of a bar that moved. */}
          {sealed && candidates.length > 0 && (
            <PromoteDetection bucketId={b.bucket.id} candidates={candidates} />
          )}
          {sealed && (
            <div className="mt-2 flex flex-wrap items-baseline gap-2">
              {/* All three read this bucket now. "Walkthrough" opened the
                  company's page - every bucket it owns, stacked, with this one
                  somewhere among them - and "Search" opened a search that
                  chose its own scope. */}
              <span className="text-caption text-ink-3">Look at it:</span>
              <Link to={`/buckets/${bucketId}/graph`} className="rounded border border-line bg-panel-2 px-2.5 py-1 text-small text-acc-2 transition-colors duration-fast hover:bg-hov">
                Discovery graph
              </Link>
              <Link to={`/buckets/${bucketId}/walkthrough`} className="rounded border border-line bg-panel-2 px-2.5 py-1 text-small text-acc-2 transition-colors duration-fast hover:bg-hov">
                Browse the records
              </Link>
              <Link to={`/buckets/${bucketId}/search`} className="rounded border border-line bg-panel-2 px-2.5 py-1 text-small text-acc-2 transition-colors duration-fast hover:bg-hov">
                Search {thisScope(prefix)}
              </Link>
            </div>
          )}
        </Step>

        <PackagingStep
          bucketId={bucketId!}
          sealed={sealed}
          isHere={here === 'package'}
          alreadyOffered={offered('package')}
        />

        {/* The rail draws six chips and the ladder drew five rows, so the one
            stage a buyer cares most about had no entry at all. Handover is
            performed from inside the package, and this says where rather than
            offering a control that is not here. */}
        <Step
          state={statusOf('deliver')}
          title={TITLES.deliver.title}
          sub={
            statusOf('deliver') === 'done'
              ? TITLES.deliver.done
              : statusOf('deliver') === 'failed'
                ? 'the last handover failed; the package page carries the reason'
                : statusOf('deliver') === 'blocked'
                  ? 'this package holds no files, so there is nothing to copy'
                  : 'happens on the package page once its gate has been read'
          }
          last
        />
      </Panel>

      {/* Neither of these is a stage, and both stood in the spine as though
          they were. The panel was headed "Also for this company" while it
          still held the company analysis control; what is left is this
          bucket's, so the heading was the last place a grouping was named. */}
      <Panel>
        <PanelHeader title={`Also for ${thisScope(prefix)}`} sub="not stages; nothing here waits on you" />
        <ReconstructionStep companyId={companyId} />
        {/* Confirming a detection asks for a reconstruction on its own, so this
            is where an operator finds out what that produced. It reads a
            request row written before anything was dispatched, which is what
            lets a pass that opened no run answer for itself. */}
        <Details title="Reconstructions asked for on this scope, and what became of them">
          <ReconstructionRequests bucketId={bucketId!} />
        </Details>
        <BucketAnalysis bucketId={bucketId!} unlocked={sealed} mayOperate={!noReadGrant} prefix={prefix} />
      </Panel>

      {/* Four analyses of a sealed walk, each a panel of its own, all opened
          at once beneath the steps: archives, shapes, domains and six series
          of measurements. Every one is worth reading and none of them is a
          decision, so stacking them under a page whose job is to say what to
          do next buried the one thing that was. They are one disclosure, shut
          until asked for.

          The largest thing in a big bucket is what no rule describes, so the
          archives lead: they are the answer to "what is actually here", and a
          corpus is worth what can be opened. */}
      {sealed && (
        <Details title="Look at what the walk found: where it came from, archives, shapes, domains and measurements">
          <div className="space-y-3 p-3">
            {/* First, because it is the only one that accounts for the files
                no rule describes: 84.6% of objects carry no tool, and most of
                them are filed under something that names where they came
                from. It answers a different question from the tool badge and
                neither overrides the other. */}
            <SourceCollections
              bucketId={bucketId!}
              scope={{
                name: bucket.data.bucket.name,
                accountId: (bucket.data as { accountId?: string }).accountId ?? null,
                region: bucket.data.bucket.region,
              }}
            />
            <Archives bucketId={bucketId!} />
            <SchemaClusters bucketId={bucketId!} />
            <DomainGraph bucketId={bucketId!} />
            <BucketCharts bucketId={bucketId!} />
          </div>
        </Details>
      )}

      {/* History is what you consult rather than what you do, and it
          stood open as a panel beside the controls. */}
      <Details title="Past observations: every recorded re-listing of this scope">
        <Panel>
          <PanelHeader title="Past observations" sub="newest first" />
        {syncs.isPending ? (
          <p className="px-4 py-3 text-small text-ink-3">Reading this bucket&rsquo;s past runs.</p>
        ) : (syncs.data?.items.length ?? 0) === 0 ? (
          <p className="px-4 py-3 text-small text-ink-3">No sync run is recorded for this scope yet.</p>
        ) : (
          syncs.data!.items.slice(0, 8).map((r) => (
            <div key={r.syncRunId} className="flex items-baseline gap-3 border-b border-line px-4 py-2 text-small last:border-0">
              <Stamp tone={r.state === 'COMPLETE' ? 'ok' : r.state === 'FAILED' || r.state === 'ACCESS_BLOCKED' ? 'bad' : 'mut'}>{say(r.state)}</Stamp>
              <span className="min-w-0 flex-1 truncate text-ink-2">Re-observed {fmt(r.requestedAt)}</span>
              <span className="mono text-caption text-ink-2">+{r.new} · ~{r.changed} · -{r.deleted} · {r.unchanged} unchanged</span>
            </div>
          ))
        )}
        </Panel>
      </Details>

      {/* Sealing is a third of the way through, and this said the pipeline
          hands off - pointing at the next bucket while this one still had
          packaging and delivery ahead of it. It belongs where the work is
          actually finished.

          It named that next bucket, too, and to find one it read every
          registered bucket in the account and then every one of their
          toolsets: a page about one bucket issuing a request per bucket the
          account has, to decide which sentence to print at the bottom. The
          work here is done and the next choice is a choice, so this says the
          one true thing and hands back to the place choices are made. */}
      {here === 'deliver' && stagesFor(railFacts).every((x) => x.status === 'done') && (
        <Panel>
          <PanelHeader title="Handed over" sub="every stage of this bucket is done; nothing waits on you here" />
          <div className="px-4 py-3 text-small text-ink-2">
            <Link to="/" className="flex items-center gap-2 text-acc-2 hover:underline">
              Work on another bucket
              <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </div>
        </Panel>
      )}
    </div>
  );
}


/**
 * Analysis of this bucket, and no other.
 *
 * This was removed rather than shipped with a warning: the only start the
 * contract offered froze a queue pooled from every bucket a company owned, so
 * pressing it on a bucket holding four tools queued twenty-one, and the
 * finished run could not be decomposed back because a tool found in two
 * buckets collapsed into one entry. The backend now freezes THIS bucket's
 * queue - "An in-flight run on a different bucket is not returned here and
 * never satisfies this request" - so the control belongs on this page again.
 *
 * It is background work, so it follows that pattern: the dispatch is held
 * until the run record answers, the run is polled only while it is moving, and
 * a finished run reads differently from one that never started.
 */
function BucketAnalysis({
  bucketId,
  unlocked,
  mayOperate,
  prefix,
}: {
  bucketId: string;
  unlocked: boolean;
  mayOperate: boolean;
  /** So this panel calls a folder a folder, like the rail above it. */
  prefix: string;
}) {
  const [runId, setRunId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const run = useQuery({
    queryKey: ['analysis-run', runId],
    queryFn: ({ signal }) => backend.getAnalysisRun(runId!, signal),
    enabled: Boolean(runId),
    retry,
    // Stops the moment the run is terminal, so a finished analysis is not
    // polled forever.
    refetchInterval: (q) => {
      const st = q.state.data?.run.state;
      return st === 'SUCCEEDED' || st === 'FAILED' ? false : 6000;
    },
  });

  const start = async () => {
    setBusy(true);
    setNote(null);
    setFailed(false);
    try {
      const r = await backend.startBucketAnalysis(bucketId, newIdempotencyKey());
      setRunId(r.run.id);
      setNote(
        `${r.disposition === 'CREATED' ? 'Started' : 'Already running'}: ${r.tools.length} ${r.tools.length === 1 ? 'tool' : 'tools'} frozen into this bucket's queue.`,
      );
    } catch (e) {
      setFailed(true);
      setNote(explainLine(e, 'start analysis for this bucket'));
    } finally {
      setBusy(false);
    }
  };

  const state = run.data?.run.state ?? null;
  const done = state === 'SUCCEEDED' || state === 'FAILED';
  const moving = Boolean(runId) && !done;
  const tools = run.data?.tools ?? [];
  const finished = tools.filter((t) => t.state === 'SUCCEEDED' || t.state === 'FAILED').length;

  return (
    <div className="border-t border-line px-4 py-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-row font-medium text-ink">Analysis</span>
        <span className="text-small text-ink-3">
          {moving
            ? `${say(state ?? 'RUNNING').toLowerCase()} · ${finished}/${tools.length} tools`
            : done
              ? `${say(state!).toLowerCase()} · ${finished} of ${tools.length} tools`
              : unlocked
                ? `runs every tool ${thisScopePossessive(prefix)} walk found, then produces the report`
                : 'available any time; most useful after a sealed walk'}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <Button variant="ghost" loading={busy || moving} disabled={!mayOperate || busy || moving} onClick={() => void start()}>
          {busy ? 'Starting…' : moving ? 'Analysing…' : done ? 'Analyse again' : 'Start analysis'}
        </Button>
        {note && (
          <span role={failed ? 'alert' : 'status'} className={`text-small ${failed ? 'text-warn' : 'text-ink-2'}`}>
            {note}
          </span>
        )}
      </div>
      {moving && (
        <Working
          what="Running every tool in this bucket's frozen queue"
          usually="a queue this size usually finishes within a few minutes"
          overAt={1800}
        />
      )}
    </div>
  );
}

/** The backend's own words for why a surface has no value yet. */
const RECONSTRUCTION_REASON: Record<string, string> = {
  NO_STRUCTURED_RECONSTRUCTION_PUBLICATION:
    'gaps are now recorded from the sealed walk and show on the package; what is still unpublished is the reconstruction itself - the records, previews, search and persona views',
  NO_RUN_SCOPED_ATTRIBUTION_BINDING: 'no analysis run is bound to a sealed attribution run yet',
  NO_COMMERCIAL_VERDICT_PUBLICATION: 'no commercial verdict has been published',
};

/**
 * Entity reconstruction is the phase after grading: sealed objects become
 * records, gap findings, and readiness verdicts.
 *
 * Gap findings are a real typed surface - severity, status, recoverability,
 * bound evidence - and none exist, because reconstruction has not published.
 * The dashboard says exactly why, so this step quotes that reason instead of
 * paraphrasing a capability flag. An empty gap list and an uncomputed one
 * are different facts and must not read the same.
 */
function ReconstructionStep({ companyId }: { companyId?: string }) {
  const caps = useCapabilities();
  const dashboard = useQuery({
    queryKey: ['company-dashboard', companyId],
    queryFn: ({ signal }) => backend.getCompanyDashboard(companyId!, signal),
    enabled: Boolean(companyId),
    retry,
    staleTime: 30_000,
  });
  const rec = caps.data?.capabilities.reconstruction;
  const open = rec === 'IMPLEMENTED';
  const block = (dashboard.data as { entityReconstruction?: { state?: string; reason?: string } } | undefined)
    ?.entityReconstruction;
  const reason = block?.reason ? (RECONSTRUCTION_REASON[block.reason] ?? say(block.reason)) : null;

  return (
    <Aside
      title="Entity reconstruction"
      sub={
        !caps.data
          ? 'reading the capability gates'
          : open && block?.state === 'AVAILABLE'
            ? 'records, gap findings, and readiness are published for sealed tools'
            : reason
              ? reason
              : 'this phase turns sealed objects into records, previews, search and the persona views; it has not run yet'
      }
    />
  );
}

/**
 * Packaging and delivery close the pipeline. The backend gates them with
 * product capabilities; until IMPLEMENTED the step states that truthfully
 * instead of hiding what comes next.
 */
function PackagingStep({
  bucketId,
  sealed,
  isHere,
  alreadyOffered,
}: {
  bucketId: string;
  sealed: boolean;
  isHere: boolean;
  /** Whether the banner above is already offering this exact route. */
  alreadyOffered: boolean;
}) {
  const caps = useCapabilities();
  const packaging = caps.data?.capabilities.packaging;
  /**
   * The capability being implemented says the product CAN package. Whether
   * THIS bucket can is a different question, and the step answered the first
   * one: on a bucket with nothing listed it drew itself current, with a live
   * control, while the listing step above it was also current. Two steps
   * claiming to be where the operator is, at opposite ends of the page.
   *
   * A package holds files a walk placed, so it waits for the seal.
   */
  const gateOpen = packaging === 'IMPLEMENTED';
  const open = gateOpen && sealed;
  return (
    <Step
      // Available and not where you are: shown as reachable rather than as
      // finished, since calling an unstarted package "done" is a worse lie
      // than calling it current.
      state={open ? (isHere ? 'current' : 'ahead') : 'ahead'}
      title={TITLES.package.title}
      sub={
        !caps.data
          ? 'reading the capability gates'
          : open
            ? 'select tools and strays, then hand the package to a delivery target'
            : gateOpen
              ? 'unlocks at seal: a package holds the files a walk placed'
              : 'this release does not offer packaging yet, so nothing here can be assembled or handed over'
      }
    >
      {open && !alreadyOffered && (
        <Link to={`/buckets/${bucketId}/package`} className="rounded border border-line bg-panel-2 px-2.5 py-1 text-small text-acc-2 transition-colors duration-fast hover:bg-hov">
          Open packaging
        </Link>
      )}
    </Step>
  );
}
